/**
 * Mobile PDP carousel: swipe, lazy loading, zoom modal, and colour
 * switching from the grouped media payload.
 *
 * Rendered by snippets/mobile-product-carousel.liquid, which also
 * emits the JSON config this reads (#mobile-gallery-config,
 * #mobile-grouped-galleries, #mobile-variant-color-map).
 */
document.addEventListener('DOMContentLoaded', () => {
  const track = document.getElementById('mobileCarouselTrack');
  const prevBtn = document.getElementById('mobilePrevBtn');
  const nextBtn = document.getElementById('mobileNextBtn');
  const indicators = document.getElementById('mobileIndicators');

  const zoomModal = document.getElementById('zoomModal');
  const zoomTrack = document.getElementById('zoomTrack');
  const zoomCloseBtn = document.getElementById('zoomCloseBtn');
  const zoomPrevBtn = document.getElementById('zoomPrevBtn');
  const zoomNextBtn = document.getElementById('zoomNextBtn');
  const zoomIndicators = document.getElementById('zoomIndicators');
  const zoomThumbnails = document.getElementById('zoomThumbnails');

  if (!track) return;

  let config = {};
  try {
    config = JSON.parse(document.getElementById('mobile-gallery-config')?.textContent || '{}');
  } catch (e) {}

  const IMAGE_SIZE = config.imageSize || 800;
  const PRODUCT_HANDLE = config.productHandle || '';

  let initialGallery = [];
  try {
    initialGallery = JSON.parse(document.getElementById('mobile-initial-gallery')?.textContent || '[]');
  } catch (e) {}

  let variantColorMap = {};
  try {
    variantColorMap = JSON.parse(document.getElementById('mobile-variant-color-map')?.textContent || '{}');
  } catch (e) {}

  const SS_PREFIX = 'mpc::' + PRODUCT_HANDLE + '::';

  // Color-keyed cache (memory)
  const galleriesCache = {};
  if (config.currentColor && initialGallery.length) {
    galleriesCache[config.currentColor] = initialGallery;
  }

  // Hydrate from sessionStorage
  function loadCachedGallery(color) {
    if (galleriesCache[color]) return galleriesCache[color];
    try {
      const raw = sessionStorage.getItem(SS_PREFIX + color);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          galleriesCache[color] = parsed;
          return parsed;
        }
      }
    } catch (e) {}
    return null;
  }

  function saveCachedGallery(color, gallery) {
    galleriesCache[color] = gallery;
    try {
      sessionStorage.setItem(SS_PREFIX + color, JSON.stringify(gallery));
    } catch (e) {}
  }

  let currentColor = config.currentColor;
  let currentVariantId = config.currentVariantId;

  // Grouped product media: every colour's set is already in the page, so the
  // colour switch below is a cache hit and the fetch path is never reached.
  let groupedData = null;
  try {
    const groupedScript = document.getElementById('mobile-grouped-galleries');
    if (groupedScript) groupedData = JSON.parse(groupedScript.textContent);
  } catch (e) {
    console.error('Failed to parse grouped media galleries:', e);
    groupedData = null;
  }

  const useGroupedMedia = !!groupedData;
  if (groupedData) {
    const galleries = groupedData.galleries || {};
    Object.keys(galleries).forEach((key) => {
      galleriesCache[key] = galleries[key].map((entry) => ({
        src: entry.url,
        poster: entry.thumb,
        alt: entry.alt || ''
      }));
    });
    variantColorMap = groupedData.variants || {};
    currentColor = groupedData.currentKey;
  }
  let currentIndex = 0;
  let isDragging = false;
  let startX = 0,
    startY = 0,
    deltaX = 0,
    deltaY = 0;
  let isHorizontal = null;
  let startTime = 0;
  // Grouped mode has no `initialGallery` (that script carries the metafield);
  // the current colour's set comes from the prefilled cache instead, so zoom
  // and swipe have the same list the server rendered.
  let currentGallery = (useGroupedMedia && galleriesCache[currentColor])
    ? galleriesCache[currentColor].slice()
    : initialGallery.slice();

  let switchAbortController = null;
  let slideObserver = null;

  // Zoom state
  let zoomIndex = 0;
  let zoomScale = 1;
  let zoomTranslateX = 0;
  let zoomTranslateY = 0;
  let isPinching = false;
  let lastDistance = 0;
  let zoomSwipeStartX = 0,
    zoomSwipeStartY = 0;
  let zoomSwipeDeltaX = 0,
    zoomSwipeDeltaY = 0;
  let isZoomSwiping = false;
  let zoomSwipeDirection = null;
  let zoomSwipeStartTime = 0;

  const MOVE_THRESHOLD = 10;
  const DIRECTION_RATIO = 1.5;
  const SWIPE_DISTANCE_RATIO = 0.15;
  const SWIPE_VELOCITY = 0.3;
  const MIN_SWIPE_DISTANCE = 50;
  const VERTICAL_CLOSE_DISTANCE = 100;

  function fixShopifyUrl(url) {
    if (!url) return url;
    let u = url;
    if (u.includes('/products/files/')) u = u.replace('/products/files/', '/cdn/shop/files/');
    if (u.startsWith('//')) return 'https:' + u;
    if (u.startsWith('http')) return u;
    if (u.startsWith('/')) return window.location.origin + u;
    return 'https://' + u;
  }

  function resizeImageUrl(url, size) {
    if (!url) return url;
    const u = fixShopifyUrl(url);
    if (!u.includes('cdn.shopify.com') && !u.includes('/cdn/shop/')) return u;
    try {
      const parsed = new URL(u);
      parsed.searchParams.set('width', size);
      parsed.searchParams.set('quality', 80);
      return parsed.toString();
    } catch (e) {
      const sep = u.includes('?') ? '&' : '?';
      return u + sep + 'width=' + size + '&quality=80';
    }
  }

  function getFullSizeUrl(url) {
    if (!url) return url;
    const u = fixShopifyUrl(url);
    if (u.includes('cdn.shopify.com') || u.includes('/cdn/shop/')) {
      return u
        .replace(/[&?]width=\d+/g, '')
        .replace(/[&?]height=\d+/g, '')
        .replace(/[&?]quality=\d+/g, '')
        .replace(/\?&/, '?')
        .replace(/\?$/, '');
    }
    return u;
  }

  function detectMedia(item) {
    if (!item) return null;
    if (item.media_type === 'video' || item.type === 'video') {
      let videoSrc = '';
      if (item.sources && Array.isArray(item.sources) && item.sources.length > 0) {
        const mp4 = item.sources.filter((s) => s.format === 'mp4' || s.mime_type === 'video/mp4');
        if (mp4.length) {
          mp4.sort((a, b) => (b.height || 0) - (a.height || 0));
          const hd = mp4.find((s) => s.height >= 720) || mp4[0];
          videoSrc = hd.url || hd.src;
        } else {
          videoSrc = item.sources[0].url || item.sources[0].src;
        }
      } else if (item.src) {
        videoSrc = item.src;
      }
      return { type: 'video', src: videoSrc, poster: item.preview_image?.src || item.poster, alt: item.alt || '' };
    }
    if (typeof item === 'string') {
      const l = item.toLowerCase();
      if (l.includes('.mp4') || l.includes('.webm') || l.includes('.mov') || l.includes('/videos/')) {
        return { type: 'video', src: item, poster: null };
      }
      return { type: 'image', src: item, alt: '' };
    }
    if (item.src) {
      const l = item.src.toLowerCase();
      if (l.includes('.mp4') || l.includes('.webm') || l.includes('.mov') || l.includes('/videos/')) {
        return { type: 'video', src: item.src, poster: item.preview_image?.src || item.poster, alt: item.alt || '' };
      }
      return { type: 'image', src: item.src, alt: item.alt || '' };
    }
    return null;
  }

  function escapeAttribute(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function createMediaElement(item, index, isZoom = false) {
    const altText = escapeAttribute(item.alt || `Product image ${index}`);
    if (item.type === 'video') {
      const videoUrl = fixShopifyUrl(item.src);
      const poster = item.poster ? fixShopifyUrl(item.poster) : '';
      const preload = isZoom || index === 1 ? 'auto' : 'metadata';
      const autoplay = isZoom || index === 1 ? 'autoplay' : '';
      const srcAttr = isZoom || index === 1 ? `src="${videoUrl}"` : `data-src="${videoUrl}"`;
      return `<video ${srcAttr} ${
        poster ? `poster="${poster}"` : ''
      } ${autoplay} muted playsinline loop preload="${preload}" style="width:100%;height:100%;object-fit:contain;"></video>`;
    }
    const url = fixShopifyUrl(item.src);
    const finalUrl = isZoom ? getFullSizeUrl(url) : resizeImageUrl(url, IMAGE_SIZE);
    if (isZoom || index === 1) {
      return `<img src="${finalUrl}" alt="${altText}" draggable="false" loading="eager" style="width:100%;height:100%;object-fit:contain;">`;
    }
    const blank = "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3C/svg%3E";
    return `<img src="${blank}" data-src="${finalUrl}" alt="${altText}" draggable="false" loading="lazy" style="width:100%;height:100%;object-fit:contain;">`;
  }

  function preloadFirstImage(gallery) {
    if (!gallery || !gallery.length) return Promise.resolve();
    const media = detectMedia(gallery[0]);
    if (!media || media.type === 'video') return Promise.resolve();
    const url = resizeImageUrl(media.src, IMAGE_SIZE);
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = resolve;
      img.src = url;
      setTimeout(resolve, 3000);
    });
  }

  function preloadGalleryRest(gallery) {
    for (let i = 1; i < gallery.length; i++) {
      const m = detectMedia(gallery[i]);
      if (!m || m.type === 'video') continue;
      const img = new Image();
      img.src = resizeImageUrl(m.src, IMAGE_SIZE);
    }
  }

  function showSkeleton() {
    let sk = document.getElementById('mpc-skeleton');
    const wrapper = track.closest('.mobile-carousel-wrapper');
    if (!wrapper) return;
    if (!sk) {
      sk = document.createElement('div');
      sk.id = 'mpc-skeleton';
      sk.className = 'mpc-skeleton';
      sk.setAttribute('aria-hidden', 'true');
      wrapper.appendChild(sk);
    }
    sk.style.display = 'block';
  }

  function hideSkeleton() {
    const sk = document.getElementById('mpc-skeleton');
    if (sk) sk.style.display = 'none';
  }

  function setupLazyObserver() {
    if (slideObserver) {
      slideObserver.disconnect();
      slideObserver = null;
    }
    if (!('IntersectionObserver' in window)) {
      // Fallback: load everything
      track.querySelectorAll('[data-src]').forEach((el) => {
        const ds = el.getAttribute('data-src');
        if (ds) {
          el.src = ds;
          el.removeAttribute('data-src');
        }
      });
      return;
    }
    slideObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const slide = entry.target;
          const media = slide.querySelectorAll('[data-src]');
          media.forEach((el) => {
            const ds = el.getAttribute('data-src');
            if (!ds) return;
            el.src = ds;
            el.removeAttribute('data-src');
            if (el.tagName === 'VIDEO') {
              el.preload = 'auto';
              try {
                el.play().catch(() => {});
              } catch (e) {}
            }
          });
          slideObserver.unobserve(slide);
        });
      },
      { root: track.parentElement, rootMargin: '50% 0px', threshold: 0.01 }
    );

    Array.from(track.children).forEach((slide) => slideObserver.observe(slide));
  }

  function rebuildCarousel(newGallery) {
    if (!newGallery || !Array.isArray(newGallery) || newGallery.length === 0) {
      track.innerHTML = `<div class="mobile-carousel-slide"><p style="color:#999;text-align:center;padding:20px;">No images available</p></div>`;
      indicators.innerHTML = '';
      hideSkeleton();
      return;
    }

    currentGallery = newGallery;
    track.innerHTML = '';

    newGallery.forEach((item, i) => {
      const media = detectMedia(item);
      if (!media) return;
      const slide = document.createElement('div');
      slide.className = 'mobile-carousel-slide';
      slide.dataset.slideIndex = i;
      const container = document.createElement('div');
      container.className = 'mobile-image-container single';
      container.innerHTML = createMediaElement(media, i + 1);
      slide.appendChild(container);
      track.appendChild(slide);
    });

    currentIndex = 0;
    rebuildIndicators();
    updateCarousel();
    attachSwipeEvents();
    setupLazyObserver();
    hideSkeleton();
  }

  function rebuildIndicators() {
    indicators.innerHTML = '';
    currentGallery.forEach((item, i) => {
      const media = detectMedia(item);
      if (!media) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `mobile-thumbnail${i === currentIndex ? ' active' : ''}`;
      btn.dataset.thumbIndex = i;
      btn.setAttribute('aria-label', `Show image ${i + 1}`);
      let thumbUrl;
      if (media.type === 'video') {
        thumbUrl = media.poster ? resizeImageUrl(media.poster, 160) : '';
      } else {
        thumbUrl = resizeImageUrl(media.src, 160);
      }
      btn.innerHTML = `<img src="${thumbUrl}" alt="" loading="lazy" draggable="false">${
        media.type === 'video' ? '<span class="mobile-thumbnail__play" aria-hidden="true">▶</span>' : ''
      }`;
      btn.addEventListener('click', () => {
        currentIndex = i;
        updateCarousel();
      });
      indicators.appendChild(btn);
    });
  }

  function scrollActiveThumbIntoView() {
    const active = indicators.querySelector('.mobile-thumbnail.active');
    if (active) active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  function updateCarousel() {
    const slides = Array.from(track.children);
    if (!slides.length) return;
    const w = slides[0].offsetWidth;
    track.style.transition = 'transform 0.5s ease';
    track.style.transform = `translateX(-${w * currentIndex}px)`;
    if (prevBtn) prevBtn.disabled = currentIndex === 0;
    if (nextBtn) nextBtn.disabled = currentIndex === slides.length - 1;
    indicators
      .querySelectorAll('.mobile-thumbnail')
      .forEach((el, i) => el.classList.toggle('active', i === currentIndex));
    scrollActiveThumbIntoView();
  }

  async function handleVariantChange(variantId) {
    if (!variantId) return;
    const newColor = variantColorMap[variantId];
    currentVariantId = variantId;

    // Size change → same color → no reload
    if (newColor === currentColor && currentGallery.length > 0) return;
    if (!newColor) return;

    // Only scroll up if gallery is above viewport
    const wrapper = track.closest('.mobile-carousel-container');
    if (wrapper) {
      const rect = wrapper.getBoundingClientRect();
      if (rect.bottom < 0) window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (switchAbortController) switchAbortController.abort();
    switchAbortController = new AbortController();
    const signal = switchAbortController.signal;
    const targetColor = newColor;

    // Cache hit (memory or sessionStorage)
    const cached = loadCachedGallery(targetColor);
    if (cached) {
      showSkeleton();
      currentColor = targetColor;
      await preloadFirstImage(cached);
      if (signal.aborted) return;
      rebuildCarousel(cached);
      preloadGalleryRest(cached);
      return;
    }

    // In grouped mode every colour that has media is already cached, so a miss
    // means this colour has none of its own. Keep what is on screen.
    if (useGroupedMedia) {
      console.warn('No grouped media for colour:', targetColor, '- keeping current gallery');
      currentColor = targetColor;
      return;
    }

    showSkeleton();

    async function tryFetch(url) {
      const resp = await fetch(url, { signal });
      if (signal.aborted) return null;
      if (!resp.ok) return null;
      const text = await resp.text();
      if (signal.aborted) return null;

      // Try parsing as JSON (alternate template response)
      const trimmed = text.trim();
      if (trimmed.startsWith('{')) {
        try {
          const data = JSON.parse(trimmed);
          if (data && Array.isArray(data.gallery)) return data.gallery;
        } catch (e) {}
      }

      // Fallback: parse full HTML response for the embedded gallery script
      try {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const scriptTag = doc.querySelector('#mobile-initial-gallery');
        if (scriptTag) {
          const parsed = JSON.parse(scriptTag.textContent || '[]');
          if (Array.isArray(parsed)) return parsed;
        }
      } catch (e) {}

      return null;
    }

    try {
      // Prefer lean alternate template; fall back to full-page fetch
      let fetched = await tryFetch(`${window.location.pathname}?variant=${variantId}&view=variant-gallery`);
      if (signal.aborted) return;
      if (!fetched || fetched.length === 0) {
        fetched = await tryFetch(`${window.location.pathname}?variant=${variantId}`);
        if (signal.aborted) return;
      }
      if (fetched && fetched.length > 0) {
        saveCachedGallery(targetColor, fetched);
        await preloadFirstImage(fetched);
        if (signal.aborted) return;
        currentColor = targetColor;
        rebuildCarousel(fetched);
        preloadGalleryRest(fetched);
        return;
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
    }

    if (!signal.aborted) {
      currentColor = targetColor;
      hideSkeleton();
      track.innerHTML = `<div class="mobile-carousel-slide"><p style="color:#999;text-align:center;padding:20px;">No images available</p></div>`;
      indicators.innerHTML = '';
    }
  }

  function attachSwipeEvents() {
    track.removeEventListener('pointerdown', pointerDown);
    track.removeEventListener('pointermove', pointerMove);
    track.removeEventListener('pointerup', pointerUp);
    track.removeEventListener('pointercancel', pointerUp);
    track.removeEventListener('pointerleave', pointerUp);

    track.addEventListener('pointerdown', pointerDown);
    track.addEventListener('pointermove', pointerMove, { passive: false });
    track.addEventListener('pointerup', pointerUp);
    track.addEventListener('pointercancel', pointerUp);
    track.addEventListener('pointerleave', pointerUp);
  }

  function pointerDown(e) {
    // Don't engage the carousel drag / tap-to-zoom when the gesture starts on a
    // Zinzo hotspot. Capturing the pointer here (setPointerCapture) retargets
    // pointerup to the track, so the tap would open the zoom modal instead of
    // the hotspot tooltip. Bailing leaves isDragging false, so pointerUp won't
    // fire openZoomModal, and the hotspot keeps its own tap/click.
    if (e.target.closest && e.target.closest('.hotspot, #zinzo-native-hotspot-overlay')) {
      isDragging = false;
      return;
    }
    isDragging = true;
    startX = e.clientX;
    startY = e.clientY;
    deltaX = 0;
    deltaY = 0;
    isHorizontal = null;
    startTime = Date.now();
    track.style.transition = 'none';
    try {
      track.setPointerCapture(e.pointerId);
    } catch (err) {}
  }

  function pointerMove(e) {
    if (!isDragging) return;
    deltaX = e.clientX - startX;
    deltaY = e.clientY - startY;
    if (isHorizontal === null) {
      if (Math.abs(deltaX) < MOVE_THRESHOLD && Math.abs(deltaY) < MOVE_THRESHOLD) return;
      isHorizontal = Math.abs(deltaX) > Math.abs(deltaY) * DIRECTION_RATIO;
      if (!isHorizontal) {
        isDragging = false;
        try {
          track.releasePointerCapture(e.pointerId);
        } catch {}
        return;
      }
    }
    e.preventDefault();
    const slides = Array.from(track.children);
    const w = slides[0]?.offsetWidth || track.offsetWidth;
    track.style.transform = `translateX(${-w * currentIndex + deltaX}px)`;
  }

  function pointerUp(e) {
    if (e.type === 'pointercancel') {
      isDragging = false;
      return;
    }
    if (!isDragging) return;
    const isClick = Math.abs(deltaX) < MOVE_THRESHOLD && Math.abs(deltaY) < MOVE_THRESHOLD;
    isDragging = false;
    const slides = Array.from(track.children);
    const w = slides[0]?.offsetWidth || track.offsetWidth;
    const elapsed = Math.max(1, Date.now() - startTime);
    const velocity = deltaX / elapsed;
    const thresh = Math.max(MIN_SWIPE_DISTANCE, w * SWIPE_DISTANCE_RATIO);
    if (isHorizontal) {
      if (deltaX > thresh || velocity > SWIPE_VELOCITY) currentIndex = Math.max(0, currentIndex - 1);
      else if (deltaX < -thresh || velocity < -SWIPE_VELOCITY)
        currentIndex = Math.min(slides.length - 1, currentIndex + 1);
    }
    updateCarousel();
    if (isClick) openZoomModal(currentIndex);
    deltaX = 0;
    deltaY = 0;
    isHorizontal = null;
    try {
      track.releasePointerCapture(e?.pointerId);
    } catch {}
  }

  if (prevBtn)
    prevBtn.addEventListener('click', () => {
      if (currentIndex > 0) {
        currentIndex--;
        updateCarousel();
      }
    });
  if (nextBtn)
    nextBtn.addEventListener('click', () => {
      const slides = track.children;
      if (currentIndex < slides.length - 1) {
        currentIndex++;
        updateCarousel();
      }
    });

  // ===== Zoom modal =====
  function openZoomModal(index) {
    if (!zoomModal) return;
    zoomIndex = index;
    resetZoomTransform();
    buildZoomSlides();
    zoomModal.classList.add('active');
    zoomModal.style.opacity = '1';
    zoomModal.style.transform = '';
    document.body.style.overflow = 'hidden';
  }

  function closeZoomModal() {
    if (!zoomModal) return;
    zoomModal.classList.remove('active');
    zoomModal.style.opacity = '';
    zoomModal.style.transform = '';
    document.body.style.overflow = '';
    resetZoomTransform();
  }

  function buildZoomSlides() {
    if (!zoomTrack) return;
    zoomTrack.innerHTML = '';
    currentGallery.forEach((item, i) => {
      const media = detectMedia(item);
      if (!media) return;
      const slide = document.createElement('div');
      slide.className = 'zoom-slide';
      slide.dataset.slideIndex = i;
      const wrapper = document.createElement('div');
      wrapper.className = 'zoom-image-wrapper';
      wrapper.innerHTML = createMediaElement(media, i + 1, true);
      slide.appendChild(wrapper);
      zoomTrack.appendChild(slide);
    });
    updateZoomSlide();
    buildZoomThumbnails();
    buildZoomIndicators();
    attachZoomEvents();
  }

  function buildZoomIndicators() {
    if (!zoomIndicators) return;
    zoomIndicators.innerHTML = '';
    currentGallery.forEach((_, i) => {
      const dot = document.createElement('div');
      dot.className = `zoom-indicator${i === zoomIndex ? ' active' : ''}`;
      dot.addEventListener('click', () => {
        zoomIndex = i;
        resetZoomTransform();
        updateZoomSlide();
      });
      zoomIndicators.appendChild(dot);
    });
  }

  function buildZoomThumbnails() {
    if (!zoomThumbnails) return;
    zoomThumbnails.innerHTML = '';
    currentGallery.forEach((item, i) => {
      const media = detectMedia(item);
      if (!media) return;
      const tw = document.createElement('div');
      tw.className = `zoom-thumbnail${i === zoomIndex ? ' active' : ''}`;
      let el;
      if (media.type === 'video') {
        const p = media.poster ? fixShopifyUrl(media.poster) : 'https://placehold.co/60x60/333333/ffffff?text=Video';
        el = `<img src="${p}" alt="Video thumbnail ${i + 1}" style="width:100%;height:100%;object-fit:cover;">`;
      } else {
        const url = resizeImageUrl(media.src, 120);
        el = `<img src="${url}" alt="Image thumbnail ${
          i + 1
        }" style="width:100%;height:100%;object-fit:cover;" loading="lazy">`;
      }
      tw.innerHTML = el;
      tw.addEventListener('click', () => {
        zoomIndex = i;
        resetZoomTransform();
        updateZoomSlide();
      });
      zoomThumbnails.appendChild(tw);
    });
  }

  function updateZoomSlide() {
    if (!zoomTrack) return;
    const slides = Array.from(zoomTrack.children);
    if (!slides.length) return;
    const w = window.innerWidth;
    zoomTrack.style.transition = 'transform 0.3s ease';
    zoomTrack.style.transform = `translateX(-${w * zoomIndex}px)`;
    if (zoomPrevBtn) zoomPrevBtn.disabled = zoomIndex === 0;
    if (zoomNextBtn) zoomNextBtn.disabled = zoomIndex === slides.length - 1;
    zoomIndicators
      ?.querySelectorAll('.zoom-indicator')
      .forEach((dot, i) => dot.classList.toggle('active', i === zoomIndex));
    zoomThumbnails
      ?.querySelectorAll('.zoom-thumbnail')
      .forEach((thumb, i) => thumb.classList.toggle('active', i === zoomIndex));
    const active = zoomThumbnails?.querySelector('.zoom-thumbnail.active');
    if (active) active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  function resetZoomTransform() {
    zoomScale = 1;
    zoomTranslateX = 0;
    zoomTranslateY = 0;
    const slide = zoomTrack?.children[zoomIndex];
    if (slide) {
      const wrap = slide.querySelector('.zoom-image-wrapper');
      if (wrap) wrap.style.transform = '';
    }
  }

  let zoomEventsAttached = false;
  function attachZoomEvents() {
    if (!zoomModal || zoomEventsAttached) return;
    zoomEventsAttached = true;

    if (zoomCloseBtn) zoomCloseBtn.addEventListener('click', closeZoomModal);
    if (zoomPrevBtn)
      zoomPrevBtn.addEventListener('click', () => {
        if (zoomIndex > 0) {
          zoomIndex--;
          resetZoomTransform();
          updateZoomSlide();
        }
      });
    if (zoomNextBtn)
      zoomNextBtn.addEventListener('click', () => {
        if (zoomIndex < currentGallery.length - 1) {
          zoomIndex++;
          resetZoomTransform();
          updateZoomSlide();
        }
      });

    zoomTrack.addEventListener('touchstart', zoomTouchStart, { passive: true });
    zoomTrack.addEventListener('touchmove', zoomTouchMove, { passive: false });
    zoomTrack.addEventListener('touchend', zoomTouchEnd);

    zoomTrack.addEventListener('touchstart', handlePinchStart, { passive: true });
    zoomTrack.addEventListener('touchmove', handlePinchMove, { passive: false });
    zoomTrack.addEventListener('touchend', handlePinchEnd);

    let lastTap = 0;
    zoomTrack.addEventListener('touchend', (e) => {
      const now = Date.now();
      if (now - lastTap < 300 && e.changedTouches.length === 1) {
        e.preventDefault();
        if (zoomScale > 1) {
          resetZoomTransform();
          const slide = zoomTrack.children[zoomIndex];
          if (slide) {
            const wrap = slide.querySelector('.zoom-image-wrapper');
            if (wrap) wrap.style.transform = 'scale(1) translate(0,0)';
          }
        } else {
          zoomScale = 2.5;
          const t = e.changedTouches[0];
          const r = zoomTrack.getBoundingClientRect();
          const x = t.clientX - r.left - r.width / 2;
          const y = t.clientY - r.top - r.height / 2;
          zoomTranslateX = -x;
          zoomTranslateY = -y;
          const slide = zoomTrack.children[zoomIndex];
          if (slide) {
            const wrap = slide.querySelector('.zoom-image-wrapper');
            if (wrap)
              wrap.style.transform = `scale(${zoomScale}) translate(${zoomTranslateX}px, ${zoomTranslateY}px)`;
          }
        }
      }
      lastTap = now;
    });

    document.addEventListener('keydown', (e) => {
      if (!zoomModal.classList.contains('active')) return;
      if (e.key === 'Escape') closeZoomModal();
      if (e.key === 'ArrowLeft' && zoomIndex > 0) {
        zoomIndex--;
        resetZoomTransform();
        updateZoomSlide();
      }
      if (e.key === 'ArrowRight' && zoomIndex < currentGallery.length - 1) {
        zoomIndex++;
        resetZoomTransform();
        updateZoomSlide();
      }
    });
  }

  function zoomTouchStart(e) {
    if (e.touches.length !== 1 || zoomScale > 1) return;
    zoomSwipeStartX = e.touches[0].clientX;
    zoomSwipeStartY = e.touches[0].clientY;
    zoomSwipeDeltaX = 0;
    zoomSwipeDeltaY = 0;
    isZoomSwiping = true;
    zoomSwipeDirection = null;
    zoomSwipeStartTime = Date.now();
  }

  function zoomTouchMove(e) {
    if (!isZoomSwiping || e.touches.length !== 1 || zoomScale > 1) return;
    zoomSwipeDeltaX = e.touches[0].clientX - zoomSwipeStartX;
    zoomSwipeDeltaY = e.touches[0].clientY - zoomSwipeStartY;
    if (zoomSwipeDirection === null) {
      if (Math.abs(zoomSwipeDeltaX) < MOVE_THRESHOLD && Math.abs(zoomSwipeDeltaY) < MOVE_THRESHOLD) return;
      zoomSwipeDirection = Math.abs(zoomSwipeDeltaX) > Math.abs(zoomSwipeDeltaY) ? 'h' : 'v';
    }
    if (zoomSwipeDirection === 'h') {
      e.preventDefault();
      const w = window.innerWidth;
      zoomTrack.style.transition = 'none';
      zoomTrack.style.transform = `translateX(${-w * zoomIndex + zoomSwipeDeltaX}px)`;
    } else if (zoomSwipeDirection === 'v' && zoomSwipeDeltaY > 0) {
      // Drag-to-close follow
      zoomModal.style.transition = 'none';
      zoomModal.style.transform = `translateY(${zoomSwipeDeltaY}px)`;
      zoomModal.style.opacity = String(Math.max(0.4, 1 - zoomSwipeDeltaY / 600));
    }
  }

  function zoomTouchEnd(e) {
    if (!isZoomSwiping || zoomScale > 1) {
      isZoomSwiping = false;
      return;
    }
    isZoomSwiping = false;

    if (zoomSwipeDirection === 'h') {
      const w = window.innerWidth;
      const elapsed = Math.max(1, Date.now() - zoomSwipeStartTime);
      const velocity = zoomSwipeDeltaX / elapsed;
      const thresh = Math.max(MIN_SWIPE_DISTANCE, w * SWIPE_DISTANCE_RATIO);
      if (zoomSwipeDeltaX > thresh || velocity > SWIPE_VELOCITY) zoomIndex = Math.max(0, zoomIndex - 1);
      else if (zoomSwipeDeltaX < -thresh || velocity < -SWIPE_VELOCITY)
        zoomIndex = Math.min(currentGallery.length - 1, zoomIndex + 1);
      resetZoomTransform();
      updateZoomSlide();
    } else if (zoomSwipeDirection === 'v') {
      zoomModal.style.transition = 'transform 0.25s ease, opacity 0.25s ease';
      if (zoomSwipeDeltaY > VERTICAL_CLOSE_DISTANCE) {
        zoomModal.style.transform = `translateY(100vh)`;
        zoomModal.style.opacity = '0';
        setTimeout(closeZoomModal, 250);
      } else {
        zoomModal.style.transform = '';
        zoomModal.style.opacity = '1';
      }
    }
    zoomSwipeDirection = null;
  }

  function handlePinchStart(e) {
    if (e.touches.length === 2) {
      isPinching = true;
      lastDistance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
    }
  }

  function handlePinchMove(e) {
    if (!isPinching || e.touches.length !== 2) return;
    e.preventDefault();
    const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
    const f = d / lastDistance;
    zoomScale = Math.min(Math.max(zoomScale * f, 1), 5);
    lastDistance = d;
    if (zoomScale <= 1) {
      zoomTranslateX = 0;
      zoomTranslateY = 0;
    }
    const slide = zoomTrack?.children[zoomIndex];
    if (slide) {
      const wrap = slide.querySelector('.zoom-image-wrapper');
      if (wrap) wrap.style.transform = `scale(${zoomScale}) translate(${zoomTranslateX}px, ${zoomTranslateY}px)`;
    }
  }

  function handlePinchEnd() {
    if (!isPinching) return;
    isPinching = false;
    if (zoomScale <= 1) {
      resetZoomTransform();
      const slide = zoomTrack?.children[zoomIndex];
      if (slide) {
        const wrap = slide.querySelector('.zoom-image-wrapper');
        if (wrap) wrap.style.transform = '';
      }
    }
  }

  // ===== Variant change listeners =====
  // The theme dispatches `on:variant:change` on each <variant-selects> element WITHOUT bubbles,
  // and updates the URL via history.replaceState. Cover both paths.
  function onVariantEvent(e) {
    const id = e.detail?.variant?.id || e.detail?.id;
    if (id) handleVariantChange(String(id));
  }

  function attachToVariantSelects() {
    document.querySelectorAll('variant-selects').forEach((el) => {
      if (el.__mpcBound) return;
      el.__mpcBound = true;
      el.addEventListener('on:variant:change', onVariantEvent);
      el.addEventListener('variant:changed', onVariantEvent);
    });
  }
  attachToVariantSelects();
  // Re-attach if the variant picker is re-rendered later
  new MutationObserver(attachToVariantSelects).observe(document.body, { childList: true, subtree: true });

  // URL-based detection (theme calls history.replaceState on variant change)
  function checkUrlVariant() {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get('variant');
    if (variantId && variantId !== currentVariantId) handleVariantChange(variantId);
  }
  const _origReplace = history.replaceState;
  history.replaceState = function () {
    _origReplace.apply(this, arguments);
    checkUrlVariant();
  };
  window.addEventListener('popstate', checkUrlVariant);

  // Bubbled fallback (some integrations dispatch on document)
  document.addEventListener('variant:changed', onVariantEvent);
  document.addEventListener('on:variant:change', onVariantEvent);

  // Initialize: server-rendered slides need swipe + lazy observer + initial gallery
  if (useGroupedMedia) {
    if (currentColor && galleriesCache[currentColor]) {
      currentGallery = galleriesCache[currentColor];
    }
  } else if (config.hasGallery && initialGallery.length) {
    currentGallery = initialGallery;
    if (currentColor) saveCachedGallery(currentColor, initialGallery);
  }
  attachSwipeEvents();
  setupLazyObserver();

  // Wire up server-rendered thumbnails
  indicators.querySelectorAll('.mobile-thumbnail').forEach((btn) => {
    const i = parseInt(btn.dataset.thumbIndex, 10);
    btn.addEventListener('click', () => {
      currentIndex = i;
      updateCarousel();
    });
  });
  if (prevBtn) prevBtn.disabled = true;
  if (nextBtn) nextBtn.disabled = track.children.length <= 1;
});
